import { useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, Moon, Search, Sparkles, Sun, User } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useCommandPalette } from "@/context/CommandPaletteContext";
import { useCopilot } from "@/context/CopilotContext";
import { useAlerts } from "@/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SeverityBadge } from "@/components/ui/badge";
import { PeriodFilter } from "@/components/layout/PeriodFilter";
import { OutsourceAccessLogo } from "@/components/brand/OutsourceAccessLogo";
import { formatRelativeTime } from "@/lib/formatters";
import { cn } from "@/lib/cn";

const ROLE_LABEL: Record<string, string> = {
  coo: "COO",
  department_head: "Department Head",
  team_leader: "Team Leader",
};

export function Header({ onOpenMobileSidebar }: { onOpenMobileSidebar: () => void }) {
  const { resolvedTheme, toggle } = useTheme();
  const { logout, identity } = useAuth();
  const navigate = useNavigate();
  const { setOpen: setPaletteOpen } = useCommandPalette();
  const { toggle: toggleCopilot } = useCopilot();
  const { data: alerts } = useAlerts();

  const openAlerts = (alerts ?? []).filter((a) => a.status === "open").slice(0, 6);
  const openAlertCount = (alerts ?? []).filter((a) => a.status === "open").length;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 bg-header-gradient px-4 shadow-sm sm:px-6">
      <button
        onClick={onOpenMobileSidebar}
        className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
      >
        <Menu className="size-5" />
      </button>

      <div className="flex shrink-0 items-center gap-2 lg:hidden">
        <OutsourceAccessLogo markOnly markClassName="size-7" />
        <span className="text-sm font-semibold text-white">OA-Mary</span>
      </div>

      <button
        onClick={() => setPaletteOpen(true)}
        className="flex h-9 max-w-md flex-1 items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 text-sm text-white/70 shadow-sm transition-colors hover:border-white/30 hover:text-white"
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">Search clients, people, alerts…</span>
        <kbd className="ml-auto hidden shrink-0 items-center gap-0.5 rounded border border-white/15 bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/70 sm:flex">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        <PeriodFilter />

        <Button variant="secondary" size="sm" onClick={toggleCopilot} className="hidden bg-white/10 text-white hover:bg-white/20 sm:inline-flex">
          <Sparkles className="size-3.5" />
          Ask AI
        </Button>
        <button
          onClick={toggleCopilot}
          className="flex size-9 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white sm:hidden"
        >
          <Sparkles className="size-4.5" />
        </button>

        <button
          onClick={toggle}
          className="flex size-9 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Toggle theme"
        >
          {resolvedTheme === "dark" ? <Sun className="size-4.5" /> : <Moon className="size-4.5" />}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button className="relative flex size-9 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white">
              <Bell className="size-4.5" />
              {openAlertCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex size-2 rounded-full bg-danger" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-foreground">Open Alerts</p>
              <span className="text-xs text-muted-foreground">{openAlertCount} open</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {openAlerts.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">No open alerts. All clear.</p>
              ) : (
                openAlerts.map((alert) => (
                  <div key={alert.id} className="flex flex-col gap-1 border-b border-border/60 px-4 py-3 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{alert.scope}</span>
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{alert.issue}</p>
                    <span className="text-[11px] text-muted-foreground">{formatRelativeTime(alert.createdAt)}</span>
                  </div>
                ))
              )}
            </div>
            <button
              onClick={() => navigate("/alerts")}
              className="w-full border-t border-border px-4 py-2.5 text-center text-xs font-medium text-brand hover:bg-accent"
            >
              View Alerts Center
            </button>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex size-9 items-center justify-center rounded-full bg-white/15 text-white transition-transform hover:scale-105 hover:bg-white/25",
              )}
            >
              <User className="size-4.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-sm font-semibold text-foreground">
                {identity ? ROLE_LABEL[identity.role] ?? identity.role : "Signed in"}
              </p>
              <p className="truncate text-xs font-normal text-muted-foreground">{identity?.email ?? "Outsource Access"}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/settings")}>Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={handleLogout}>
              <LogOut className="size-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
